import {
  computed,
  effect,
  signal,
  untracked,
  type ReadonlySignal,
  type Signal,
} from "@preact/signals-core";

type Ready<T> = { type: "ready"; data: T };

export type StateType<T> =
  | { type: "initialized" }
  | { type: "pending" }
  | Ready<T>
  | { type: "error"; error: Error };

type SubscribeCallback<T> = (newState: StateType<T>) => void;

export class State<T> {
  private stateSignal: Signal<StateType<T>>;
  private pendingPromises: Array<{
    resolve: (value: T) => void;
    reject: (error: Error) => void;
  }> = [];
  // private setQueue: Array<() => Promise<T>> = [];
  // private isProcessing = false;
  private processing = new Map<Promise<T>, /** order */ number>();
  private setOrder = 0;
  private finishedCount = 0;

  constructor(initialValue?: T | Promise<T>) {
    const hasInitialValue = initialValue !== undefined;
    this.stateSignal = signal<StateType<T>>(
      !hasInitialValue ? { type: "initialized" } : { type: "pending" }
    );

    if (hasInitialValue) {
      this.set(initialValue);
    }

    effect(() => {
      const currentState = this.stateSignal.value;
      if (currentState.type === "ready") {
        this.resolvePendingPromises(currentState.data);
      } else if (currentState.type === "error") {
        this.rejectPendingPromises(currentState.error);
      }
    });
  }

  private resolvePendingPromises(value: T) {
    const promises = [...this.pendingPromises];
    this.pendingPromises = [];
    for (const promise of promises) {
      promise.resolve(value);
    }
  }

  private rejectPendingPromises(error: Error) {
    const promises = [...this.pendingPromises];
    this.pendingPromises = [];
    for (const promise of promises) {
      promise.reject(error);
    }
  }

  set(valueOrPromise: T | Promise<T>) {
    if (this.stateSignal.value.type !== "pending") {
      this.stateSignal.value = { type: "pending" };
    }

    const currentPromise = (async () => valueOrPromise)();
    const currentOrder = this.setOrder;
    this.processing.set(currentPromise, currentOrder);
    this.setOrder += 1;

    currentPromise
      .then((value) => {
        if (
          [...this.processing.entries()].every(
            ([, order]) => order <= currentOrder
          )
        ) {
          this.stateSignal.value = { type: "ready", data: value };
        }
      })
      .catch((err) => {
        if (
          [...this.processing.entries()].every(
            ([, order]) => order <= currentOrder
          )
        ) {
          this.stateSignal.value = {
            type: "error",
            error: err instanceof Error ? err : new Error(String(err)),
          };
        }
      })
      .finally(() => {
        this.finishedCount += 1;
        if (this.finishedCount === this.processing.size) {
          this.processing.clear();
          this.finishedCount = 0;
        }
      });

    // eslint-disable-next-line no-console
    // this.processSetQueue().catch(console.log);
  }

  async get(): Promise<T> {
    const signal = this.stateSignal;
    const currentState = untracked(() => signal.value);

    if (currentState.type === "ready") {
      return Promise.resolve(currentState.data);
    } else if (currentState.type === "error") {
      return Promise.reject(currentState.error);
    } else {
      return new Promise<T>((resolve, reject) => {
        this.pendingPromises.push({ resolve, reject });
      });
    }
  }

  getState(): StateType<T> {
    return this.stateSignal.value;
  }

  subscribe(callback: SubscribeCallback<T>): () => void {
    return this.stateSignal.subscribe(callback);
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export class Computed<T extends readonly any[], U> {
  private stateSignal: ReadonlySignal<StateType<U>>;
  private pendingPromises: Array<{
    resolve: (value: U) => void;
    reject: (error: Error) => void;
  }> = [];

  constructor(
    arr: {
      [K in keyof T]: State<T[K]>;
    },
    private computeState: (states: { [K in keyof T]: T[K] }) => U
  ) {
    this.stateSignal = computed((): StateType<U> => {
      const signals = arr.map((state) => state.getState());

      if (signals.some((signal) => signal.type === "initialized")) {
        return { type: "initialized" };
      }

      if (signals.some((signal) => signal.type === "error")) {
        return {
          type: "error",
          error: new Error("One or more states are in error state"),
        };
      }

      if (signals.some((signal) => signal.type === "pending")) {
        return { type: "pending" };
      }

      const readyData = signals.reduce(
        (acc, signal, index) => {
          acc[index as keyof T] = (signal as Ready<T>).data;
          return acc;
        },
        {
          length: signals.length,
          *[Symbol.iterator]() {
            for (let i = 0; i < this.length; i++) {
              yield this[i];
            }
          },
        } as { [K in keyof T]: T[K] }
      );

      return {
        type: "ready",
        data: this.computeState(readyData),
      };
    });

    effect(() => {
      const currentState = this.stateSignal.value;
      if (currentState.type === "ready") {
        this.resolvePendingPromises(currentState.data);
      } else if (currentState.type === "error") {
        this.rejectPendingPromises(currentState.error);
      }
    });
  }

  private resolvePendingPromises(value: U) {
    const promises = [...this.pendingPromises];
    this.pendingPromises = [];
    for (const promise of promises) {
      promise.resolve(value);
    }
  }

  private rejectPendingPromises(error: Error) {
    const promises = [...this.pendingPromises];
    this.pendingPromises = [];
    for (const promise of promises) {
      promise.reject(error);
    }
  }

  async get(): Promise<U> {
    const currentState = this.stateSignal.value;

    if (currentState.type === "ready") {
      return Promise.resolve(currentState.data);
    } else if (currentState.type === "error") {
      return Promise.reject(currentState.error);
    } else {
      return new Promise<U>((resolve, reject) => {
        this.pendingPromises.push({ resolve, reject });
      });
    }
  }

  getState(): StateType<U> {
    return this.stateSignal.value;
  }

  subscribe(callback: SubscribeCallback<U>): () => void {
    return this.stateSignal.subscribe(callback);
  }
}
